import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../utils/firebase";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
  addDoc,
  arrayUnion
} from "firebase/firestore";
import {
  Grid,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Avatar,
  IconButton,
  Tooltip,
  Dialog
} from "@mui/material";

import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import { useAuth } from "../context/AuthContext";

// Helper to calculate age from dob string (YYYY-MM-DD)
function getAge(dob) {
  if (!dob) return null;
  const birth = new Date(dob);
  if (isNaN(birth)) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

function AdminEventsPanel({ events = [], loading = false }) {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addEventId, setAddEventId] = useState(null);
  const [participantForm, setParticipantForm] = useState({ name: '', email: '', gender: '', dob: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const handleAddParticipantClick = () => {};

  const handleParticipantFormChange = (e) => {
    setParticipantForm({ ...participantForm, [e.target.name]: e.target.value });
  };

  const handleAddParticipant = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      // Prevent accidentally adding the current admin account as a participant
      const { user } = useAuth();
      if (user && participantForm && participantForm.email && user.email === participantForm.email) {
        throw new Error('Admins cannot join events.');
      }
      // 1. Create participant doc
      const participantData = {
        name: participantForm.name,
        email: participantForm.email,
        gender: participantForm.gender,
        dob: participantForm.dob,
        eventId: addEventId,
        createdAt: new Date(),
      };
      const participantRef = await addDoc(collection(db, "participants"), participantData);
      // 2. Add participant ID to event's participants array
      await updateDoc(doc(db, "events", addEventId), {
        participants: arrayUnion(participantRef.id)
      });
      setAddDialogOpen(false);
    } catch (err) {
      setError("Failed to add participant: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", mt: 4 }}>
      <Typography variant="h4" gutterBottom>Events Admin Panel</Typography>
      {loading ? (
        <CircularProgress />
      ) : events.length === 0 ? (
        <Typography>No events found.</Typography>
      ) : (
        <Grid container spacing={4}>
          {events.map(ev => (
            <Grid item xs={12} sm={6} md={4} key={ev.id}>
              <Box
                onClick={() => navigate(`/admin/event/${ev.id}`)}
                sx={{
                  cursor: 'pointer',
                  height: 340,
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: 4,
                  boxShadow: 4,
                  p: 1,
                  bgcolor: '#fff',
                  transition: 'box-shadow 0.2s, transform 0.2s',
                  '&:hover': {
                    boxShadow: 8,
                    transform: 'translateY(-2px) scale(1.02)',
                  },
                }}
                tabIndex={0}
                role="button"
                aria-label={`View details for event ${ev.name}`}
              >
                <CardHeader
                  avatar={ev.imageUrl ? <Avatar src={ev.imageUrl} sx={{ width: 56, height: 56 }} /> : <Avatar sx={{ width: 56, height: 56 }}>{ev.name?.[0] || "E"}</Avatar>}
                  title={<Typography variant="h5" fontWeight={700} sx={{
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    wordBreak: 'break-word',
                    maxHeight: 60,
                    lineHeight: 1.2,
                    display: 'block',
                  }}>{ev.name}</Typography>}
                  subheader={(() => {
                    if (!ev || !ev.date) return '';
                    let d;
                    if (ev.date && typeof ev.date.seconds === 'number') d = new Date(ev.date.seconds * 1000);
                    else if (ev.date && typeof ev.date.toDate === 'function') d = ev.date.toDate();
                    else d = new Date(ev.date);
                    return isNaN(d) ? '' : d.toLocaleDateString();
                  })()}
                  sx={{ pb: 0 }}
                />
                <CardContent sx={{ flexGrow: 1, pt: 1 }}>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    <b>General Location:</b> {ev.generalLocation || "TBA"}
                  </Typography>
                  <Typography variant="subtitle1" color="text.secondary" gutterBottom>
                    <b>Specific Location:</b> {ev.specificLocation || "TBA"}
                  </Typography>
                  <Typography variant="body1" color="text.primary" sx={{ mb: 1 }}>
                    <b>Summary:</b> {ev.summary || ev.description || "No summary provided."}
                  </Typography>
                  <Box sx={{ mt: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="body2" sx={{ color: '#1976d2', fontWeight: 600 }}>
                      Men: {ev.maleCount}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#d81b60', fontWeight: 600 }}>
                      Women: {ev.femaleCount}
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#333', fontWeight: 600 }}>
                      Total: {ev.totalCount}
                    </Typography>
                  </Box>
                </CardContent>
                <Box sx={{ height: 20 }} />
              </Box>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
}

export default AdminEventsPanel;


